"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface Destination {
  name: string;
  distance: string;
  description: string;
}
const DESTINATIONS: Destination[] = [{
  name: "Hurghada City",
  distance: "10-15 Min. vom Flughafen",
  description: "Das lebhafte Stadtzentrum verbindet Geschäftszentren, traditionelle Märkte und zentrale Resorts."
}, {
  name: "El Mamsha",
  distance: "8-12 Min. vom Flughafen",
  description: "Fußgängerpromenade gesäumt von Strandhotels, lebhaften Straßencafés und Boutiquen."
}, {
  name: "Hurghada Marina",
  distance: "15 Min. vom Flughafen",
  description: "Exklusiver Uferboulevard mit Yachthafen, erstklassigen Restaurants am Wasser und Flaniermeile."
}, {
  name: "Sheraton Road",
  distance: "12-15 Min. vom Flughafen",
  description: "Belebte Hauptverkehrsader mit Anbindung an den Stadtteil Sakala und direktem Strandzugang."
}, {
  name: "Dahar",
  distance: "20 Min. vom Flughafen",
  description: "Historisches Altstadtviertel, bekannt für traditionelle Souks, Gewürzbasare und authentisches Flair."
}, {
  name: "Senzo Mall",
  distance: "15 Min. vom Flughafen",
  description: "Großes Einkaufszentrum im Süden und praktischer Verkehrsknotenpunkt für Reisende entlang der Küste."
}, {
  name: "El Gouna",
  distance: "30-35 Min. vom Flughafen",
  description: "Gehobene Lagunenstadt im Norden mit Meisterschaftsgolfplätzen und exklusiven Yachthäfen."
}, {
  name: "Sahl Hasheesh",
  distance: "25 Min. vom Flughafen",
  description: "Küstenort mit türkisfarbenem Wasser, einer Strandpromenade und Ferienresorts."
}, {
  name: "Makadi Bay",
  distance: "35 Min. vom Flughafen",
  description: "Ruhige Küstenoase im Süden, geschätzt für familienfreundliche All-Inclusive-Resorts am Strand."
}, {
  name: "Soma Bay",
  distance: "45-50 Min. vom Flughafen",
  description: "Malerische südliche Halbinsel, bekannt für unberührte Korallenriffe, Kitesurfen und Wellness-Spas."
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
};
export default function PopularDestinations() {
  const handleScrollToBooking = () => {
    document.getElementById("privatecardriverhero")?.scrollIntoView({
      behavior: "smooth"
    });
  };
  return <section id="populardestinations" data-nav="light" className="bg-secondary/40 text-foreground py-16 md:py-24 border-y border-border/60">
      <div className="max-w-[1136px] mx-auto px-6">
        {/* Header Block */}
        <div className="flex flex-col items-start gap-4 mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="inline-block size-2 rounded-full bg-primary" aria-hidden="true" />
            <span>Stundenweise Abdeckung mit Privatfahrer</span>
          </div>

          <div className="w-full pb-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-accent-foreground font-default">
              Beliebte Regionen und Reiseziele vor Ort
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Stundenweiser Privatfahrer- und Chauffeurservice für alle erstklassigen Resorts, Geschäftsviertel und Küstenregionen.
            </p>
          </div>
        </div>

        {/* 10-Item Destinations Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-40px"
      }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
          {DESTINATIONS.map((dest, i) => <motion.div key={dest.name} data-index={i} variants={cardVariants} className="h-full">
              <div className="group relative flex flex-col justify-between h-full p-4 sm:p-5 rounded-2xl bg-card border border-border transition-all duration-200 hover:-translate-y-1 hover:border-primary/80 hover:shadow-md">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="flex items-center justify-center size-8 rounded-full bg-accent/60 text-accent-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                      <MapPin className="size-4 text-primary group-hover:text-primary-foreground transition-colors duration-200" />
                    </span>
                    <span className="text-[11px] font-medium text-muted-foreground bg-background/80 px-2 py-0.5 rounded-full border border-border/50">
                      {dest.distance}
                    </span>
                  </div>

                  <h3 className="font-semibold text-sm sm:text-base text-accent-foreground group-hover:text-foreground transition-colors">
                    {dest.name}
                  </h3>

                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground line-clamp-3">
                    {dest.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-border/50">
                  <Button type="button" variant="ghost" size="sm" onClick={handleScrollToBooking} className="w-full h-auto p-0 justify-between text-xs font-medium text-accent-foreground hover:bg-transparent hover:text-primary transition-colors cursor-pointer group/btn">
                    <span>Privatfahrer anfragen</span>
                    <ArrowRight className="size-3.5 transition-transform duration-200 group-hover/btn:translate-x-0.5 text-primary" />
                  </Button>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Closing Call-to-Action Bar */}
        <div className="mt-10 sm:mt-12 p-6 sm:p-8 rounded-2xl bg-card border border-border flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="flex items-center gap-4 text-left w-full sm:w-auto">
            <span className="shrink-0 flex items-center justify-center size-12 rounded-full bg-accent text-accent-foreground">
              <Navigation className="size-6 text-accent-foreground" />
            </span>
            <div>
              <p className="text-base font-semibold text-accent-foreground">
                Reisen Sie an ein anderes Ziel am Roten Meer?
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                Wir bieten private Fernfahrten nach Kairo, Luxor, Marsa Alam sowie maßgeschneiderte Transfers in ganz Ägypten.
              </p>
            </div>
          </div>

          <div className="w-full sm:w-auto shrink-0 flex items-center">
            <Button asChild className="w-full sm:w-auto rounded-full px-6 py-2.5 font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs transition-all">
              <Link to="#privatecardriverhero">
                <span>Fahrt anfragen</span>
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}